// Cross-input validation: sheet x audio zips x images zip.
// Produces the model the confirm screen / import engine consumes.

import { LIMITS, LANG_ORDER } from "./constants";
import { blocker, warning, info, counts } from "./issues";
import { imageKind } from "./parseZips";

export function slugify(s) {
  return String(s || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

// Serialized size of the tourPoints/create body we would send for this stop.
export function stopPayloadBytes(stop, textLangs, audioLangs, tourId = 999999, placeId = 999999) {
  // Fake filenames mirror the server's real shape: Date.now() prefix on BOTH kinds.
  const payload = buildStopPayload(stop, textLangs, audioLangs, tourId, placeId, {
    image: `1753900000000t${tourId}_s${String(stop.sl).padStart(2, "0")}.jpg`,
    audio: Object.fromEntries(audioLangs.map((l) => [l, `1753900000000t${tourId}_s${String(stop.sl).padStart(2, "0")}_${l}.mp3`])),
  });
  return new TextEncoder().encode(JSON.stringify(payload)).length;
}

// The exact body shape POST /api/tourPoints/create expects (mirrors the admin's
// AddStopsComponent payload; note the load-bearing DB spellings).
// Text (name/discription) always covers ALL sheet languages; the audio map only
// the selected audio languages — remaining languages' audio is added later via
// tourPoints/update without touching the text.
export function buildStopPayload(stop, textLangs, audioLangs, tourId, placeId, uploaded) {
  const textOrder = LANG_ORDER.filter((l) => textLangs.includes(l));
  const audioOrder = LANG_ORDER.filter((l) => audioLangs.includes(l));
  return {
    placeId,
    tourId,
    name: textOrder.map((l) => ({ language: l, text: stop.names[l] || "" })),
    discription: textOrder.map((l) => ({ language: l, text: wrapParagraphs(stop.descriptions[l] || "") })),
    thumbnailUrl: uploaded.image,
    stopLanguagesMap: audioOrder.map((l) => ({ language: l, file: uploaded.audio[l] || "" })),
    stopLanguagesPreviewMap: [], // production norm — always an empty array, never null
    audioLock: !!stop.audioLock,
    isHideStop: stop.hideStop ? 1 : 0,
    longitude: String(stop.lng),
    lattitude: String(stop.lat),
    radius: String(stop.radius),
  };
}

// Plain sheet text -> the <p>-wrapped HTML the admin's Quill editor produces.
// Blank lines separate paragraphs; single newlines (Excel's Alt+Enter) become <br>
// so intended line breaks survive HTML rendering.
export function wrapParagraphs(text) {
  const paras = String(text).split(/\n{2,}|\r\n\r\n/).map((p) => p.trim()).filter(Boolean);
  if (!paras.length) return "";
  return paras.map((p) => `<p>${escapeHtml(p).replace(/\r?\n/g, "<br>")}</p>`).join("");
}

function escapeHtml(s) {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/**
 * @param sheet     result of parseSheet()
 * @param audioZips array of parseAudioZip() results
 * @param imagesZip result of parseImagesZip() or null
 * @param zipIssues the issues array the zip parsers pushed into (folded in so the
 *                  returned ok/counts are authoritative — callers must not re-merge)
 * @param opts      {audioLangs?: string[]} — which languages' AUDIO this run imports.
 *                  Explicit: a missing zip for a selected language is a blocker.
 *                  Omitted: inferred from the provided zips; sheet languages without
 *                  audio become warnings (text still imports for every sheet tab).
 */
export function validateAll(sheet, audioZips, imagesZip, zipIssues = [], opts = {}) {
  const issues = [...sheet.issues, ...zipIssues];
  const languages = sheet.languages;

  // ---- audio zips + audio-language selection ----
  const byLang = new Map();
  for (const zip of audioZips) {
    if (!zip.lang) continue; // already a blocker from the zip parser
    if (byLang.has(zip.lang)) issues.push(blocker("dup-lang-zip", `Two audio zips for language "${zip.lang}" (${byLang.get(zip.lang).name} and ${zip.name})`));
    else byLang.set(zip.lang, zip);
    if (!languages.includes(zip.lang)) issues.push(warning("zip-lang-not-in-sheet", `[${zip.name}] language "${zip.lang}" has no tab in the sheet — it will be ignored`));
  }

  let audioLangs;
  if (opts.audioLangs && opts.audioLangs.length) {
    audioLangs = LANG_ORDER.filter((l) => opts.audioLangs.includes(l));
    for (const l of opts.audioLangs) {
      if (!languages.includes(l)) issues.push(blocker("audio-lang-not-in-sheet", `--langs includes "${l}" but the sheet has no ${l} tab`));
      else if (!byLang.has(l)) issues.push(blocker("missing-lang-zip", `No audio zip provided for ${l} (selected for this import)`, { lang: l }));
    }
  } else {
    audioLangs = LANG_ORDER.filter((l) => languages.includes(l) && byLang.has(l));
    for (const lang of languages) {
      if (!byLang.has(lang)) issues.push(warning("lang-without-audio", `No audio zip for ${lang} — stops will import WITHOUT ${lang} audio (its text still imports; add the audio later)`, { lang }));
    }
  }
  if (!audioLangs.length) {
    issues.push(blocker("no-audio-langs", "No audio languages at all — a tour with zero audio would be invisible in the app's language selection"));
  }
  for (const zip of byLang.values()) {
    if (languages.includes(zip.lang) && !audioLangs.includes(zip.lang)) {
      issues.push(warning("zip-not-selected", `[${zip.name}] provided but ${zip.lang} is not in --langs — it will be ignored this run`));
    }
  }

  // ---- per-stop coverage + content checks ----
  for (const stop of sheet.stops) {
    // audio per selected language
    for (const lang of audioLangs) {
      const zip = byLang.get(lang);
      if (!zip) continue;
      const f = zip.files.get(stop.sl);
      if (!f) {
        issues.push(blocker("missing-audio", `stop ${stop.sl}: no ${lang} audio in ${zip.name}`, { sl: stop.sl, lang }));
        continue;
      }
      stop.audio[lang] = f;
      if (f.size === 0) issues.push(blocker("empty-audio", `stop ${stop.sl} ${lang}: audio file is 0 bytes`, { sl: stop.sl, lang }));
      if (f.size > LIMITS.audioBytes) issues.push(blocker("audio-too-big", `stop ${stop.sl} ${lang}: ${(f.size / 1e6).toFixed(1)} MB exceeds the server's 50,000,000-byte upload limit`, { sl: stop.sl, lang }));
      // slug vs EN name sanity (mis-numbered zip detector). Compare on letters+digits
      // only — "Merchant’s Bridge" -> merchant-s-bridge vs an exporter's merchants-bridge
      // must NOT fire (dash placement differs, content matches).
      const nameKey = slugify(stop.names.en).replace(/-/g, "");
      const slugKey = (f.slug || "").replace(/-/g, "");
      if (nameKey && slugKey && !(slugKey.includes(nameKey) || nameKey.includes(slugKey))) {
        issues.push(warning("slug-mismatch", `stop ${stop.sl} ${lang}: file "${f.entryName}" slug doesn't resemble "${stop.names.en}" — check the numbering`, { sl: stop.sl, lang }));
      }
    }
    // orphan audio (files with SL numbers the sheet doesn't have)
    // image — materialized here (small) so magic-check, preview, and upload share the bytes
    if (imagesZip) {
      const img = imagesZip.files.get(stop.sl);
      if (!img) issues.push(blocker("missing-image", `stop ${stop.sl}: no image (expected ${stop.sl}.jpg/.png in ${imagesZip.name})`, { sl: stop.sl }));
      else {
        if (img.size > LIMITS.imageBytes) {
          issues.push(blocker("image-too-big", `stop ${stop.sl}: image ${(img.size / 1e6).toFixed(1)} MB exceeds the server's 5,000,000-byte upload limit`, { sl: stop.sl }));
        } else {
          const data = img.getData();
          const kind = imageKind(data);
          if (!kind) issues.push(blocker("bad-image", `stop ${stop.sl}: "${img.entryName}" is not a decodable JPEG/PNG`, { sl: stop.sl }));
          else stop.image = { ...img, data, ext: kind === "png" ? "png" : "jpg" };
        }
      }
    }
  }
  if (!imagesZip) issues.push(blocker("no-images-zip", "No images zip provided — one numbered image per stop is required"));

  // orphan files in zips
  const validSl = new Set(sheet.stops.map((s) => s.sl));
  for (const zip of byLang.values()) {
    for (const sl of zip.files.keys()) {
      if (!validSl.has(sl)) issues.push(warning("orphan-audio", `[${zip.name}] file for stop ${sl} but the sheet has no such stop`, { sl, lang: zip.lang }));
    }
  }
  if (imagesZip) {
    for (const sl of imagesZip.files.keys()) {
      if (!validSl.has(sl)) issues.push(warning("orphan-image", `[${imagesZip.name}] image ${sl} but the sheet has no such stop`, { sl }));
    }
  }

  // ---- manifest cross-checks (selected zips only) ----
  for (const zip of [...byLang.values()].filter((z) => audioLangs.includes(z.lang))) {
    const mStops = zip.manifest?.stops;
    if (!Array.isArray(mStops)) {
      if (!zip.manifest) issues.push(info("no-manifest", `[${zip.name}] no manifest.json — duration checks skipped for ${zip.lang}`));
      continue;
    }
    for (const ms of mStops) {
      const sl = Number(ms.position);
      if (!validSl.has(sl)) continue;
      const audio = (ms.audios || []).find((a) => a.language === zip.lang) || (ms.audios || [])[0];
      const dur = audio?.durationSeconds;
      if (typeof dur === "number" && dur < LIMITS.minAudioSeconds) {
        issues.push(warning("short-audio", `stop ${sl} ${zip.lang}: only ${Math.round(dur)}s of audio (manifest) — verify the file`, { sl, lang: zip.lang }));
      }
    }
  }

  // ---- descriptions that already contain HTML/entities would be double-escaped
  // and shown literally in the app — warn, don't corrupt silently ----
  for (const stop of sheet.stops) {
    for (const [lang, desc] of Object.entries(stop.descriptions)) {
      if (/&[a-zA-Z#]\w*;|<[a-zA-Z][^>]*>/.test(desc || "")) {
        issues.push(warning("html-in-description", `stop ${stop.sl} (${lang}): description contains HTML tags or entities — they will display as literal text; paste plain text instead`, { sl: stop.sl, lang }));
      }
    }
  }

  // ---- payload size pre-check (backend effective JSON limit) ----
  for (const stop of sheet.stops) {
    const bytes = stopPayloadBytes(stop, languages, audioLangs);
    if (bytes > LIMITS.maxJsonBytes) {
      issues.push(blocker("payload-too-big", `stop ${stop.sl}: create request would be ${(bytes / 1024).toFixed(0)} KB (backend JSON limit ~100 KB) — shorten the descriptions`, { sl: stop.sl }));
    }
  }

  const c = counts(issues);
  return { model: { languages, audioLangs, stops: sheet.stops }, issues, ok: c.blockers === 0, counts: c };
}


